// import { useEffect, useState, useCallback, useRef } from 'react';
// import { useDispatch } from 'react-redux';

// /**
//  * Хук для загрузки опций по выбранному ключевому полю, управления доступностью селектора
//  * и сброса указанных полей в formData при отсутствии значения ключевого поля.
//  *
//  * @param {object} formData - Объект данных формы, содержащий ключевую информацию для загрузки опций.
//  * @param {function} setFormData - Функция для обновления formData.
//  * @param {Array<string>} resetFields - Массив имён полей в formData, которые будут сброшены при сбросе ключевого поля.
//  * @param {string} keyField - Имя поля в formData, используемого для загрузки опций.
//  * @param {function} fetchOptions - Функция для загрузки опций с сервера.
//  * @param {string} selectMessage - Сообщение для отображения при изменениях селектора.
//  * @returns {object} Объект с состояниями: isSelectorDisabled, message и onSelectorChange.
//  */
// function useOptionsLoader(formData, setFormData, resetFields, keyField, fetchOptions, selectMessage) {
//   const dispatch = useDispatch(); // Hook для доступа к dispatch функции Redux.
//   const [isSelectorDisabled, setIsSelectorDisabled] = useState(!formData[keyField]); // Состояние доступности селектора.
//   const [message, setMessage] = useState(''); // Сообщение, отображаемое пользователю.
//   const previousKeyFieldValue = useRef(formData[keyField]); // Хранение предыдущего значения ключевого поля

//   // Используем useRef для хранения актуального selectMessage
//   const selectMessageRef = useRef(selectMessage);

//   // Обновляем ref при изменении selectMessage, но не обновляем state
//   useEffect(() => {
//     selectMessageRef.current = selectMessage;

//     // Если сообщение уже установлено, обновляем его для актуального языка
//     if (message) {
//       setMessage(selectMessage);
//     }
//   }, [selectMessage, message]);

//   // Используем useCallback для обновления onSelectorChange с использованием selectMessageRef
//   const onSelectorChange = useCallback(() => {
//     setMessage(selectMessageRef.current); // Установка актуального сообщения при клике на селектор
//   }, []); // Пустой массив зависимостей, чтобы использовать всегда актуальное значение из useRef

//   useEffect(() => {
//     if (formData[keyField]) {
//       if (previousKeyFieldValue.current !== formData[keyField]) {
//         // Если значение ключевого поля изменилось, сбрасываем зависимые поля
//         setFormData(
//           (prevFormData) =>
//             resetFields.reduce((acc, fieldName) => ({ ...acc, [fieldName]: '' }), {
//               ...prevFormData,
//             }) // Сброс зависимых полей
//         );
//       }
//       // Если ключевое поле установлено, загружаем данные и разрешаем использование селектора.
//       setMessage(''); // Очищаем сообщение.
//       dispatch(fetchOptions(formData[keyField])); // Диспатчим функцию для загрузки данных.
//       setIsSelectorDisabled(false); // Разрешаем использование селектора.
//     } else {
//       // Если ключевое поле не установлено, сбрасываем зависимые поля и блокируем селектор.
//       setFormData(
//         (prevFormData) => resetFields.reduce((acc, fieldName) => ({ ...acc, [fieldName]: '' }), prevFormData) // Сброс указанных полей.
//       );
//       setIsSelectorDisabled(true); // Блокируем селектор.
//     }
//     // Следим за изменениями в ключевом поле и перезапускаем эффект при их изменении.
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [formData[keyField]]);

//   return { isSelectorDisabled, message, onSelectorChange };
// }

// export default useOptionsLoader;
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Хук для загрузки опций по выбранному ключевому полю, управления доступностью селектора
 * и сброса указанных полей в formData при отсутствии значения ключевого поля.
 *
 * @param {object} formData - Объект данных формы, содержащий ключевую информацию для загрузки опций.
 * @param {function} setFormData - Функция для обновления formData.
 * @param {Array<string>} resetFields - Массив имён полей в formData, которые будут сброшены при сбросе ключевого поля.
 * @param {string} keyField - Имя поля в formData, используемого для загрузки опций.
 * @param {function} fetchOptions - Функция для загрузки опций с сервера.
 * @param {string} selectMessage - Сообщение для отображения при изменениях селектора.
 * @param {boolean} [skipResetOnce=false] - Пропустить сброс при первом запуске (например, при инициализации формы).
 * @returns {object} Объект с состояниями: isSelectorDisabled, message и onSelectorChange.
 */
function useOptionsLoader(
  formData,
  setFormData,
  resetFields,
  keyField,
  fetchOptions,
  selectMessage,
  skipResetOnce = false
) {
  const dispatch = useDispatch();
  const [isSelectorDisabled, setIsSelectorDisabled] = useState(!formData[keyField]); // Состояние доступности селектора.
  const [message, setMessage] = useState(''); // Сообщение, отображаемое пользователю.
  const previousKeyFieldValue = useRef(formData?.[keyField]); // Храним предыдущее значение ключевого поля.
  const hasSkippedReset = useRef(skipResetOnce); // Флаг, использованный для пропуска сброса на первом запуске.

  const selectMessageRef = useRef(selectMessage); // Ref для selectMessage, чтобы всегда использовать актуальное значение.

  // Слежение за изменением текста сообщения
  useEffect(() => {
    selectMessageRef.current = selectMessage;
    if (message) {
      setMessage(selectMessage);
    }
  }, [selectMessage, message]);

  // Функция для отображения сообщения при открытии селектора
  const onSelectorChange = useCallback(() => {
    setMessage(selectMessageRef?.current);
  }, []);

  // Главная логика: отслеживаем изменение ключевого поля
  useEffect(() => {
    const currentValue = formData?.[keyField];
    const hasChanged = previousKeyFieldValue.current !== currentValue;

    if (currentValue) {
      // Если значение изменилось и мы не игнорируем сброс
      if (hasChanged && !hasSkippedReset.current) {
        setFormData((prevFormData) =>
          resetFields?.reduce(
            (acc, fieldName) => ({ ...acc, [fieldName]: '' }),
            { ...prevFormData }
          )
        );
      }

      hasSkippedReset.current = false; // Сбросили флаг после первого использования

      setMessage('');
      dispatch(fetchOptions(currentValue));
      setIsSelectorDisabled(false);
    } else {
      // Если поле не заполнено, сбрасываем зависимые и блокируем селектор
      setFormData((prevFormData) =>
        resetFields?.reduce((acc, fieldName) => ({ ...acc, [fieldName]: '' }), prevFormData)
      );
      setIsSelectorDisabled(true);
    }

    previousKeyFieldValue.current = currentValue;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.[keyField]]);

  return { isSelectorDisabled, message, onSelectorChange };
}

export default useOptionsLoader;
